import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { useSite } from "../context/SiteContext";

const ext = (u) => /^(https?:|tel:|mailto:)/.test(u || "");
const L = ({ to, children, className, testId }) => ext(to) ? <a href={to} className={className} data-testid={testId} target={to.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{children}</a> : <Link to={to || "/"} className={className} data-testid={testId}>{children}</Link>;

export default function Footer() {
  const { settings: s, footerSections } = useSite();
  if (!s) return null;
  const f = s.footer || {};
  const social = Object.entries(s.social || {}).filter(([, v]) => v);
  return (
    <footer className="bg-[#07221A] text-gray-300 mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center gap-3">
            <img src={f.logo_url || s.logo_url} alt={s.short_name} className="w-11 h-11 rounded-xl border border-emerald-700/40" data-testid="footer-logo" />
            <div><p className="font-serif-luxury text-2xl font-bold text-white">{s.short_name}</p><p className="text-xs text-emerald-300/80">{s.tagline}</p></div>
          </div>
          <p className="text-xs text-gray-400 leading-relaxed max-w-md" data-testid="footer-about">{f.about_text}</p>
          {social.length > 0 && <div className="flex flex-wrap gap-3">{social.map(([k, v]) => <a key={k} href={v} target="_blank" rel="noreferrer" data-testid={`footer-social-${k}`} className="text-[11px] font-bold uppercase tracking-wider text-amber-400 hover:text-amber-300">{k}</a>)}</div>}
        </div>
        {(footerSections || []).map((sec) => (
          <div key={sec.id} className="space-y-3" data-testid={`footer-section-${sec.id}`}>
            <h4 className="text-xs font-bold uppercase tracking-wider text-amber-400">{sec.title}</h4>
            <ul className="space-y-2 text-xs">{(sec.links || []).map((l, i) => <li key={i}><L to={l.url} className="hover:text-white transition-colors">{l.label}</L></li>)}</ul>
          </div>
        ))}
        <div className="space-y-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-amber-400">Centre & Contact</h4>
          <div className="space-y-2 text-xs">
            <p className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" /><span data-testid="footer-address">{s.address}</span></p>
            <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-emerald-400" /><a href={`tel:${s.primary_phone}`} className="hover:text-white" data-testid="footer-phone">{s.primary_phone}{s.secondary_phone ? ` / ${s.secondary_phone}` : ""}</a></p>
            <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-emerald-400" /><a href={`mailto:${s.email}`} className="hover:text-white" data-testid="footer-email">{s.email}</a></p>
            <p className="flex items-start gap-2"><Clock className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" /><span>{s.opening_hours}</span></p>
          </div>
        </div>
      </div>
      <div className="border-t border-emerald-900/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex flex-col md:flex-row justify-between gap-3 text-[11px] text-gray-500">
          <div className="space-y-1"><p data-testid="footer-copyright">{(f.copyright || "").replace("{year}", new Date().getFullYear())}</p>{f.disclaimer && <p className="text-gray-600 max-w-xl">{f.disclaimer}</p>}</div>
          <div className="flex flex-wrap gap-4">{(f.bottom_links || []).map((l, i) => <L key={i} to={l.url} className="hover:text-gray-200" testId={`footer-bottom-link-${i}`}>{l.label}</L>)}</div>
        </div>
      </div>
    </footer>
  );
}
